<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  useTemplateRef,
  ref,
  getCurrentInstance,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import MessageMeta from '../MessageMeta.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const { attachment, appearance, showMessageMeta, compact } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
  appearance: {
    type: String,
    default: 'incoming',
    validator: value => ['incoming', 'outgoing', 'forwarded'].includes(value),
  },
  showMessageMeta: {
    type: Boolean,
    default: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const WAVEFORM_BAR_COUNT = 42;
const MIN_WAVEFORM_BAR_HEIGHT = 14;
const SEEK_INTERVAL_MS = 50;
const waveformCache = new Map();

defineOptions({
  inheritAttrs: false,
});

const timeStampURL = computed(() => {
  return timeStampAppendedURL(attachment.dataUrl);
});
const playbackURL = ref(timeStampURL.value);

const audioPlayer = useTemplateRef('audioPlayer');
const waveformRef = useTemplateRef('waveformRef');

const isPlaying = ref(false);
const isSeeking = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const waveformBars = ref(
  Array.from({ length: WAVEFORM_BAR_COUNT }, () => MIN_WAVEFORM_BAR_HEIGHT)
);

let waveformAbortController;
let waveformObjectURL;
let playbackAnimationFrame = null;
let seekAnimationFrame = null;
let pendingSeekTime = null;
let lastSeekCommitAt = 0;
let playbackTimelineStartedAt = null;
let playbackTimelineOffset = 0;

const { uid } = getCurrentInstance();

const updateDuration = () => {
  const audioDuration = audioPlayer.value?.duration;
  if (Number.isFinite(audioDuration) && audioDuration > 0) {
    duration.value = audioDuration;
  }
};

const useDecodedAudioForPlayback = (audioData, responseContentType) => {
  if (isPlaying.value) return;

  const contentType =
    responseContentType ||
    attachment.contentType ||
    attachment.content_type ||
    'audio/ogg';

  if (waveformObjectURL) URL.revokeObjectURL(waveformObjectURL);

  waveformObjectURL = URL.createObjectURL(
    new Blob([audioData], { type: contentType })
  );
  playbackURL.value = waveformObjectURL;
};

useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || !Number.isFinite(time)) return '0:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Tempo exibido embaixo do waveform: enquanto não tocou nada, mostra a
// duração total (igual WhatsApp num áudio recebido). Quando dá play, mostra
// o tempo corrente.
const displayTime = computed(() => {
  if (isPlaying.value || currentTime.value > 0) return currentTime.value;
  return duration.value;
});

const waveformFromAudio = audioBuffer => {
  const samples = audioBuffer.getChannelData(0);
  const chunkSize = Math.ceil(samples.length / WAVEFORM_BAR_COUNT);
  const strengths = Array.from({ length: WAVEFORM_BAR_COUNT }, (_, index) => {
    const start = index * chunkSize;
    const end = Math.min(samples.length, start + chunkSize);
    const step = Math.max(1, Math.floor((end - start) / 200));
    let sum = 0;
    let count = 0;

    for (let sampleIndex = start; sampleIndex < end; sampleIndex += step) {
      sum += samples[sampleIndex] ** 2;
      count += 1;
    }

    return Math.sqrt(sum / Math.max(count, 1));
  });
  const peak = Math.max(...strengths, Number.EPSILON);

  return strengths.map(strength => {
    const normalized = Math.min(1, strength / peak);
    return Math.round(
      MIN_WAVEFORM_BAR_HEIGHT +
        Math.sqrt(normalized) * (100 - MIN_WAVEFORM_BAR_HEIGHT)
    );
  });
};

const loadWaveform = async () => {
  const cachedWaveform = waveformCache.get(attachment.dataUrl);
  if (cachedWaveform) {
    waveformBars.value = cachedWaveform;
    return;
  }

  waveformAbortController = new AbortController();

  try {
    const response = await fetch(timeStampURL.value, {
      credentials: 'same-origin',
      signal: waveformAbortController.signal,
    });
    if (!response.ok) return;

    const AudioContextConstructor =
      window.AudioContext || window.webkitAudioContext;
    if (!AudioContextConstructor) return;

    const audioContext = new AudioContextConstructor();
    try {
      const audioData = await response.arrayBuffer();
      useDecodedAudioForPlayback(
        audioData,
        response.headers.get('content-type')
      );
      const audioBuffer = await audioContext.decodeAudioData(
        audioData.slice(0)
      );
      const bars = waveformFromAudio(audioBuffer);
      waveformCache.set(attachment.dataUrl, bars);
      waveformBars.value = bars;
      if (Number.isFinite(audioBuffer.duration) && audioBuffer.duration > 0) {
        duration.value = audioBuffer.duration;
      }
    } finally {
      audioContext.close();
    }
  } catch {
    // O player nativo continua sendo usado se a origem não permitir análise.
  } finally {
    waveformAbortController = null;
  }
};

onMounted(() => {
  updateDuration();
  loadWaveform();
});

onBeforeUnmount(() => {
  waveformAbortController?.abort();
  if (waveformObjectURL) URL.revokeObjectURL(waveformObjectURL);
  if (playbackAnimationFrame !== null) {
    cancelAnimationFrame(playbackAnimationFrame);
  }
  if (seekAnimationFrame !== null) {
    cancelAnimationFrame(seekAnimationFrame);
  }
});

// Quantos bars marcar como "já ouvidos" — proporcional ao progresso.
const progressIndex = computed(() => {
  if (!duration.value || !Number.isFinite(currentTime.value)) return 0;
  return Math.round((currentTime.value / duration.value) * WAVEFORM_BAR_COUNT);
});

// Posição da bolinha verde (em %) sobre o waveform. Quando não há
// duração ainda ou nada foi tocado, fica colada no início (0%).
const playheadPercent = computed(() => {
  if (!duration.value || !Number.isFinite(currentTime.value)) return 0;
  return Math.min(100, Math.max(0, (currentTime.value / duration.value) * 100));
});

const onTimeUpdate = () => {
  if (!isSeeking.value && !isPlaying.value) {
    currentTime.value = audioPlayer.value?.currentTime;
  }
};

const stopPlaybackAnimation = () => {
  if (playbackAnimationFrame === null) return;

  cancelAnimationFrame(playbackAnimationFrame);
  playbackAnimationFrame = null;
};

const syncPlaybackTimeline = time => {
  playbackTimelineStartedAt = performance.now();
  playbackTimelineOffset = time;
};

const updatePlaybackPosition = () => {
  if (!isPlaying.value || !audioPlayer.value) return;

  if (!isSeeking.value) {
    const elapsed = (performance.now() - playbackTimelineStartedAt) / 1000;
    const visualTime = Math.min(
      duration.value || Number.POSITIVE_INFINITY,
      playbackTimelineOffset + elapsed
    );
    const playerTime = audioPlayer.value.currentTime;
    currentTime.value = Math.max(
      Number.isFinite(playerTime) ? playerTime : 0,
      visualTime
    );
  }
  playbackAnimationFrame = requestAnimationFrame(updatePlaybackPosition);
};

const startPlaybackAnimation = (time = currentTime.value) => {
  stopPlaybackAnimation();
  syncPlaybackTimeline(time);
  updatePlaybackPosition();
};

const commitPendingSeek = () => {
  if (pendingSeekTime === null || !audioPlayer.value) return;

  const seekTime = pendingSeekTime;
  audioPlayer.value.currentTime = seekTime;
  currentTime.value = seekTime;
  pendingSeekTime = null;
  lastSeekCommitAt = performance.now();
  if (isPlaying.value) syncPlaybackTimeline(seekTime);
};

const flushPendingSeek = () => {
  if (seekAnimationFrame !== null) {
    cancelAnimationFrame(seekAnimationFrame);
    seekAnimationFrame = null;
  }

  commitPendingSeek();
};

const scheduleSeek = () => {
  if (seekAnimationFrame !== null) return;

  seekAnimationFrame = requestAnimationFrame(() => {
    seekAnimationFrame = null;
    if (
      isSeeking.value &&
      performance.now() - lastSeekCommitAt < SEEK_INTERVAL_MS
    ) {
      scheduleSeek();
      return;
    }

    commitPendingSeek();
  });
};

const seekToPosition = clientX => {
  if (!duration.value || !waveformRef.value || !audioPlayer.value) return;
  const rect = waveformRef.value.getBoundingClientRect();
  const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  const time = ratio * duration.value;
  pendingSeekTime = time;
  currentTime.value = time;
  scheduleSeek();
};

const startSeeking = event => {
  if (!duration.value) return;

  isSeeking.value = true;
  event.currentTarget.setPointerCapture(event.pointerId);
  seekToPosition(event.clientX);
};

const continueSeeking = event => {
  if (isSeeking.value) seekToPosition(event.clientX);
};

const stopSeeking = event => {
  if (!isSeeking.value) return;

  seekToPosition(event.clientX);
  flushPendingSeek();
  isSeeking.value = false;
  if (event.currentTarget.hasPointerCapture(event.pointerId)) {
    event.currentTarget.releasePointerCapture(event.pointerId);
  }
};

const onPlaying = () => {
  if (!isPlaying.value) {
    isPlaying.value = true;
    startPlaybackAnimation();
  }
};

const onPause = () => {
  isPlaying.value = false;
  stopPlaybackAnimation();
};

const onCanPlay = () => {
  updateDuration();
};

const onPlaybackError = () => {
  isPlaying.value = false;
  stopPlaybackAnimation();
};

const playOrPause = async () => {
  const player = audioPlayer.value;
  if (!player) return;

  if (isPlaying.value) {
    player.pause();
    return;
  }

  emitter.emit('pause_playing_audio', uid);
  player.muted = false;
  player.volume = 1;
  isPlaying.value = true;
  startPlaybackAnimation();

  try {
    await player.play();
  } catch {
    onPlaybackError();
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  stopPlaybackAnimation();
};
</script>

<template>
  <audio
    ref="audioPlayer"
    :src="playbackURL"
    controls
    class="hidden"
    playsinline
    preload="metadata"
    @loadedmetadata="updateDuration"
    @durationchange="updateDuration"
    @canplay="onCanPlay"
    @timeupdate="onTimeUpdate"
    @playing="onPlaying"
    @pause="onPause"
    @error="onPlaybackError"
    @ended="onEnd"
  />
  <div
    v-bind="$attrs"
    class="acl-wa-audio rounded-xl w-full flex flex-col"
    :class="[
      `acl-wa-audio--${appearance}`,
      { 'acl-wa-audio--compact': compact },
    ]"
  >
    <slot name="audio-header" />
    <div class="acl-wa-audio__row">
      <slot name="audio-leading" />
      <button
        type="button"
        class="acl-wa-audio__play"
        :class="isPlaying ? 'is-playing' : 'is-paused'"
        :aria-label="isPlaying ? 'Pausar áudio' : 'Reproduzir áudio'"
        @click="playOrPause"
      >
        <Icon
          v-if="isPlaying"
          class="size-6"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-6" icon="i-teenyicons-play-small-solid" />
      </button>
      <div class="acl-wa-audio__column translate-y-2">
        <div
          ref="waveformRef"
          class="acl-wa-audio__waveform"
          :class="{ 'is-seeking': isSeeking }"
          @pointerdown="startSeeking"
          @pointermove="continueSeeking"
          @pointerup="stopSeeking"
          @pointercancel="stopSeeking"
        >
          <div
            v-for="(h, i) in waveformBars"
            :key="i"
            class="acl-wa-audio__bar"
            :class="{ 'is-played': i < progressIndex }"
            :style="{ '--waveform-bar-height': h + '%' }"
          />
          <span
            class="acl-wa-audio__playhead"
            :style="{ left: playheadPercent + '%' }"
            aria-hidden="true"
          />
        </div>
        <div class="acl-wa-audio__meta">
          <span class="tabular-nums">{{ formatTime(displayTime) }}</span>
          <MessageMeta
            v-if="showMessageMeta"
            class="acl-wa-audio__messagemeta"
          />
        </div>
      </div>
      <slot name="audio-trailing" />
    </div>

    <div
      v-if="attachment.transcribedText && showTranscribedText"
      class="text-n-slate-12 p-3 mt-2 text-sm bg-n-alpha-1 rounded-lg w-full break-words"
    >
      {{ attachment.transcribedText }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.acl-wa-audio {
  width: 400px;
  min-width: 360px;
  max-width: 430px;
  padding: 9px 11px 8px;
}

.acl-wa-audio--forwarded {
  padding-top: 28px;
}

.acl-wa-audio--outgoing {
  background: transparent;
}

.acl-wa-audio.acl-wa-audio--compact {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding: 0;
}

.acl-wa-audio--compact .acl-wa-audio__row,
.acl-wa-audio--compact .acl-wa-audio__column {
  min-width: 0;
}

.acl-wa-audio--compact .acl-wa-audio__waveform {
  overflow: hidden;
}

.acl-wa-audio__row {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
}
.acl-wa-audio__play {
  width: 32px;
  height: 32px;
  display: grid;
  place-content: center;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: inherit;
  flex-shrink: 0;
  border-radius: 50%;
  transition: background 0.12s;
}
.acl-wa-audio__play:hover {
  background: rgba(0, 0, 0, 0.06);
}

.acl-wa-audio__play.is-playing > *,
.acl-wa-audio__play.is-paused > * {
  display: none;
}

.acl-wa-audio__play.is-paused::before {
  content: '';
  width: 0;
  height: 0;
  margin-left: 2px;
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
  border-left: 11px solid currentColor;
}

.acl-wa-audio__play.is-playing::before {
  content: '';
  width: 11px;
  height: 14px;
  background: linear-gradient(
    to right,
    currentColor 0 4px,
    transparent 4px 7px,
    currentColor 7px 11px
  );
}
.acl-wa-audio__column {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.acl-wa-audio__waveform {
  position: relative;
  width: 100%;
  min-width: 0;
  height: 30px;
  display: flex;
  align-items: center;
  gap: 2px;
  cursor: pointer;
  touch-action: none;
}
.acl-wa-audio__waveform.is-seeking {
  cursor: grabbing;
}
.acl-wa-audio__waveform.is-seeking .acl-wa-audio__playhead {
  transition: none;
}
.acl-wa-audio__bar {
  flex: 1 1 0;
  min-width: 2px;
  background: rgba(71, 85, 105, 0.25);
  border-radius: 2px;
  transition: background 0.08s linear;
}
.acl-wa-audio__bar.is-played {
  background: #53bdeb;
}
.acl-wa-audio__playhead {
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  margin-left: -6px;
  background: #53bdeb;
  border-radius: 50%;
  box-shadow: none;
  transform: translateY(-50%);
  pointer-events: none;
  transition: left 0.08s linear;
}
.acl-wa-audio__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  font-size: 11px;
  line-height: 1.2;
  color: rgba(0, 0, 0, 0.55);
  padding: 3px 0 0;
}
.acl-wa-audio__meta > .tabular-nums {
  margin-right: auto;
}
.acl-wa-audio__messagemeta {
  font-size: 11px;
  line-height: 1.2;
  color: inherit;
}

:deep(.dark) .acl-wa-audio__play:hover {
  background: rgba(255, 255, 255, 0.08);
}
:deep(.dark) .acl-wa-audio__bar {
  background: rgba(255, 255, 255, 0.28);
}
:deep(.dark) .acl-wa-audio__bar.is-played {
  background: rgba(255, 255, 255, 0.55);
}
:deep(.dark) .acl-wa-audio__playhead {
  box-shadow: none;
}
:deep(.dark) .acl-wa-audio__meta {
  color: rgba(255, 255, 255, 0.55);
}

.acl-wa-audio--outgoing .acl-wa-audio__bar {
  background: rgba(74, 124, 86, 0.28);
}

/* Mantém a onda visível mesmo com as regras globais da personalização. */
.acl-wa-audio.acl-wa-audio.acl-wa-audio .acl-wa-audio__bar {
  height: var(--waveform-bar-height) !important;
}
</style>
